import { useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect } from "react";

import { customFetch } from "../util/customFetch";

const mountNode = (item) => ({
  id: item.id,
  name: item.name,
  type: item.documentType,
  parentId: item.parent?.id || "root",
  path: item.path,
});

const mountHierarchy = (item) => ({
  id: item.id,
  children: null,
});

const updateNodeChildren = (nodes, nodeId, newChildren) => {
  return nodes.map((node) => {
    if (node.id === nodeId) {
      return { ...node, children: newChildren };
    }
    if (node.children?.length > 0) {
      return {
        ...node,
        children: updateNodeChildren(node.children, nodeId, newChildren),
      };
    }
    return node;
  });
};

const useTreeFolder = () => {
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(true);
  const [treeData, setTreeData] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [allNodes, setAllNodes] = useState({});
  const [loadingNodes, setLoadingNodes] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      const data = await queryClient.fetchQuery({
        staleTime: 0,
        gcTime: 0,
        queryKey: ["treeChildren", "root"],
        queryFn: () =>
          customFetch(`/api/documentos/{tenant}/documents?isPageable=false`),
      });
      setAllNodes((prev) => {
        return {
          ...prev,
          ...data.content.reduce((acc, item) => {
            return { ...acc, [item.id]: mountNode(item) };
          }, {}),
        };
      });
      setTreeData((prevState) => [
        ...prevState,
        ...data.content.map(mountHierarchy),
      ]);
      setLoading(false);
    };
    fetch();
  }, []);

  const handleToggle = useCallback(
    async (node) => {
      const newExpandedNodes = new Set(expandedNodes);

      if (newExpandedNodes.has(node.id)) {
        newExpandedNodes.delete(node.id);
      } else {
        newExpandedNodes.add(node.id);

        if (node.children === null) {
          try {
            setLoadingNodes((prev) => new Set(prev).add(node.id));
            const cachedData = queryClient.getQueryData([
              "treeChildren",
              node.id,
            ]);
            if (cachedData) {
              setAllNodes((prev) => {
                return {
                  ...prev,
                  ...cachedData.content.reduce((acc, item) => {
                    return { ...acc, [item.id]: mountNode(item) };
                  }, {}),
                };
              });
              setTreeData((prevData) =>
                updateNodeChildren(
                  prevData,
                  node.id,
                  cachedData.content.map(mountHierarchy),
                ),
              );
            } else {
              queryClient
                .fetchQuery({
                  staleTime: 0,
                  gcTime: 0,
                  queryKey: ["treeChildren", node.id],
                  queryFn: () =>
                    customFetch(
                      `/api/documentos/{tenant}/documents?parentId=${node.id}&onlyFolder=true&isPageable=false`,
                    ),
                })
                .then((data) => {
                  setAllNodes((prev) => {
                    return {
                      ...prev,
                      ...data.content.reduce((acc, item) => {
                        return { ...acc, [item.id]: mountNode(item) };
                      }, {}),
                    };
                  });
                  setTreeData((prevData) =>
                    updateNodeChildren(
                      prevData,
                      node.id,
                      data.content.map(mountHierarchy),
                    ),
                  );
                });
            }
          } catch (error) {
            console.error(
              `Failed to fetch children for node ${node.id}:`,
              error,
            );
          } finally {
            setLoadingNodes((prev) => {
              const newLoading = new Set(prev);
              newLoading.delete(node.id);
              return newLoading;
            });
          }
        }
      }

      setExpandedNodes(newExpandedNodes);
    },
    [expandedNodes, updateNodeChildren, queryClient],
  );

  const handleSelectNode = useCallback((node) => {
    setSelectedNode(node);
  }, []);

  return {
    loading,
    treeData,
    allNodes,
    expandedNodes,
    setSelectedNode,
    selectedNode,
    loadingNodes,
    handleToggle,
    handleSelectNode,
  };
};

export default useTreeFolder;
